import { mdiFormatListBulleted } from "@mdi/js";
import { GridLegacy, useTheme } from "@mui/material";
import { MiButton } from "../MiButton/MiButton";
import { MiPlaceholderIcon } from "../MiPlaceholderIcon/MiPlaceholderIcon";

/**
 * Renders a placeholder list component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.text - The text to display in the placeholder.
 * @param {string} [props.helperText] - The helper text to display below the placeholder.
 * @param {string} [props.iconColor] - The color of the placeholder icon.
 * @param {string} [props.icon=mdiFormatListBulleted] - The icon to display in the placeholder.
 * @param {Array<Object>} [props.actions=[]] - The actions to display below the placeholder.
 *
 * @param {string} props.actions[].title - The title of the action.
 * @param {string} props.actions[].icon - The icon of the action.
 * @param {boolean} props.actions[].inverse - Determines whether to display the action in inverse style.
 * @param {Function} props.actions[].onClick - The function to call when the action is clicked.
 * @param {string} props.actions[].color - The color of the action button.
 *
 * @returns {JSX.Element} The rendered placeholder list component.
 */

export function MiPlaceholderList({
  text = "",
  helperText = "",
  iconColor = "",
  iconBgColor = "",
  image,
  textProps,
  helperTextProps,
  icon = mdiFormatListBulleted,
  actions = [],
  containerStyle,
}) {
  const theme = useTheme();

  return (
    <GridLegacy
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
      style={{ height: "100%", ...containerStyle }}
    >
      <GridLegacy item>
        <MiPlaceholderIcon
          image={image}
          icon={icon}
          iconColor={iconColor}
          iconBgColor={iconBgColor}
          text={text}
          helperText={helperText}
          textProps={textProps}
          helperTextProps={helperTextProps}
        />
      </GridLegacy>
      <GridLegacy item>
        <GridLegacy sx={{ paddingTop: theme.spacing(2) }} container>
          {actions.map((action) => (
            <GridLegacy item key={action.title}>
              <MiButton
                title={action.title}
                onClick={action.onClick}
                variant={action.variant}
                color={action.color}
                disabled={action.disabled}
                startIcon={
                  action.startIcon && {
                    path: action.startIcon,
                    iconSize: 0.8,
                  }
                }
                endIcon={
                  action.endIcon && {
                    path: action.endIcon,
                    iconSize: 0.8,
                  }
                }
              />
            </GridLegacy>
          ))}
        </GridLegacy>
      </GridLegacy>
    </GridLegacy>
  );
}
